/**
 * VozForm — "Tu voz": todo lo que decide CÓMO se escribe el mensaje.
 *
 * Antes este paso era una sola pregunta (tono: Formal/Amigable/Divertido) y debajo una frase de
 * ejemplo escrita a mano en el front. Esa frase no era ninguna plantilla real: se elegía el tono
 * mirando algo que no iba a salir. Todo lo demás —el trato de vos/tú/usted, los emojis, el largo,
 * si el mensaje pide algo, quién firma— estaba enterrado en el código: el 🍣 le llegaba igual a
 * una peluquería y el voseo a un cliente de México.
 *
 * Ahora cada decisión es un parámetro, y el ejemplo sale del GENERADOR REAL del backend
 * (`POST /activa/voz/ensayo`), así que no puede divergir del mensaje que se manda. Debajo, el
 * desglose muestra de qué partes está hecho y qué perilla gobierna cada una — el dueño puede ver
 * por qué su mensaje dice lo que dice en vez de discutir con una caja negra.
 */

import { useState } from "react";
import { MessageCircle } from "lucide-react";
import type { ContextReal, VozReal } from "../types.real";
import type { EnsayoVoz } from "../hooks/useEnsayoVoz";

interface Props {
  value: ContextReal;
  onChange: (v: ContextReal) => void;
  /** para que el ensayo diga el nombre real del local en vez de "tu marca" */
  marca?: string;
  /**
   * El ensayo lo calcula ahora el wizard (hook useEnsayoVoz) y lo muestra pegado abajo, para
   * que acompañe también al paso de la oferta. Acá se recibe ya hecho: VozForm sólo dibuja el
   * DESGLOSE ("¿por qué dice eso?"), que explica el mensaje que se ve abajo.
   */
  ensayo?: EnsayoVoz | null;
}

const TONOS = [
  { k: "Formal", desc: "Trato cuidado, sin emojis de más." },
  { k: "Amigable", desc: "Cercano y directo. El más usado." },
  { k: "Divertido", desc: "Suelto, con chiste. Para marcas jóvenes." },
];

const TRATAMIENTOS = [
  { k: "vos", label: "Vos", desc: "Argentina, Uruguay, Paraguay" },
  { k: "tu", label: "Tú", desc: "México, Chile, Perú, España" },
  { k: "usted", label: "Usted", desc: "Trato formal o público mayor" },
];

const EMOJIS = [
  { k: "ninguno", label: "Sin emojis", desc: "Estudios, salud, servicios formales" },
  { k: "pocos", label: "Alguno", desc: "Uno por mensaje" },
  { k: "muchos", label: "Varios", desc: "Gastronomía, retail joven" },
];

const LARGOS = [
  { k: "corto", label: "Corto", desc: "Una línea. Rinde más en WhatsApp." },
  { k: "completo", label: "Completo", desc: "Con la frase de cierre." },
];

/** Emojis de rubro más comunes. Reemplaza al 🍣 que estaba fijo para todos. */
const RUBROS = [
  { e: "🍣", n: "Sushi" }, { e: "🍕", n: "Pizza" }, { e: "🍔", n: "Burgers" },
  { e: "☕", n: "Café" }, { e: "🍷", n: "Bar" }, { e: "🍰", n: "Pastelería" },
  { e: "💇", n: "Peluquería" }, { e: "💅", n: "Estética" }, { e: "🏋️", n: "Gimnasio" },
  { e: "👟", n: "Indumentaria" }, { e: "🐶", n: "Mascotas" }, { e: "", n: "Ninguno" },
];

/**
 * CTAs listos, ya escritos con los tokens de conjugación del backend ({v:…}), para que cambiar el
 * tratamiento también los conjugue. Escribir uno a mano es válido, pero entonces el texto sale tal
 * cual — por eso los presets están primero.
 */
const CTAS = [
  { txt: "", label: "Sin pedido" },
  { txt: "{v:escribinos} por acá y {v:telo} preparamos", label: "Que escriba por WhatsApp" },
  { txt: "{v:pedi} por acá cuando quieras", label: "Que haga el pedido" },
  { txt: "{v:veni} cuando quieras, {v:te} esperamos", label: "Que venga al local" },
  { txt: "{v:reserva} {v:tu} lugar respondiendo este mensaje", label: "Que reserve" },
];

function Grupo({ titulo, sub, children }: { titulo: string; sub: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2.5">
      <div>
        <p className="text-sm font-bold text-slate-900">{titulo}</p>
        <p className="text-xs text-slate-500">{sub}</p>
      </div>
      {children}
    </div>
  );
}

function Opciones({ opts, value, onChange }: { opts: { k: string; label?: string; desc?: string }[]; value?: string; onChange: (k: string) => void }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
      {opts.map((o) => (
        <button
          key={o.k}
          type="button"
          onClick={() => onChange(o.k)}
          aria-pressed={value === o.k}
          className={`text-left px-3 py-2.5 rounded-xl border text-sm transition-all focus-visible:ring-2 focus-visible:ring-violet-400 ${
            value === o.k ? "bg-violet-50 border-violet-400 ring-1 ring-violet-200" : "bg-white border-slate-200 hover:border-violet-300"
          }`}
        >
          <span className={`block font-semibold ${value === o.k ? "text-violet-800" : "text-slate-700"}`}>{o.label || o.k}</span>
          {o.desc && <span className="block text-[11px] text-slate-500 mt-0.5 leading-snug">{o.desc}</span>}
        </button>
      ))}
    </div>
  );
}

export default function VozForm({ value, onChange, ensayo }: Props) {
  const voz: VozReal = value.voz || {};
  const setVoz = (patch: Partial<VozReal>) => onChange({ ...value, voz: { ...voz, ...patch } });

  const [ctaLibre, setCtaLibre] = useState(!CTAS.some((c) => c.txt === (voz.cta || "")));
  const [prohibidasTxt, setProhibidasTxt] = useState((voz.prohibidas || []).join(", "));

  return (
    <div className="space-y-6">
      {/* El "Así suena" ya no vive acá: lo muestra el wizard pegado abajo (AsiSuena), para que
          acompañe también al paso de la oferta y no se pierda al scrollear estos controles.
          VozForm se queda con las perillas y con el desglose que explica ese mensaje. */}

      {/* ── de qué partes está hecho ── */}
      {!!ensayo?.desglose?.length && (
        <details className="rounded-2xl border border-slate-100 bg-slate-50/60 p-4">
          <summary className="text-sm font-semibold text-slate-700 cursor-pointer select-none">
            ¿Por qué dice eso? — las {ensayo.desglose.length} partes del mensaje
          </summary>
          <div className="mt-3 space-y-2">
            {ensayo.desglose.map((p, i) => (
              <div key={i} className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-3 text-xs">
                <span className="font-mono text-[10px] uppercase tracking-wider text-violet-600 sm:w-16 flex-shrink-0">{p.parte}</span>
                <span className="text-slate-700 flex-1">{p.texto}</span>
                <span className="text-slate-500 sm:text-right sm:w-56 flex-shrink-0">{p.lo_decide}</span>
              </div>
            ))}
          </div>
        </details>
      )}

      {/* ── las perillas ── */}
      <Grupo titulo="¿Cómo le hablás?" sub="El tono define el gancho de cada mensaje.">
        <Opciones opts={TONOS.map((t) => ({ k: t.k, desc: t.desc }))} value={value.tono} onChange={(k) => onChange({ ...value, tono: k as any })} />
      </Grupo>

      <Grupo titulo="¿De vos, de tú o de usted?" sub="Conjuga todos los verbos del mensaje.">
        <Opciones opts={TRATAMIENTOS} value={voz.tratamiento || "vos"} onChange={(k) => setVoz({ tratamiento: k as any })} />
      </Grupo>

      <Grupo titulo="¿Cuántos emojis?" sub="Un estudio contable y un after office no escriben igual.">
        <Opciones opts={EMOJIS} value={voz.emojis || "pocos"} onChange={(k) => setVoz({ emojis: k as any })} />
      </Grupo>

      <Grupo titulo="¿Qué tan largo?" sub="En WhatsApp, corto suele rendir más.">
        <Opciones opts={LARGOS} value={voz.largo || "completo"} onChange={(k) => setVoz({ largo: k as any })} />
      </Grupo>

      <Grupo titulo="El emoji de tu rubro" sub="Se usa en los mensajes que lo llevan.">
        <div className="flex flex-wrap gap-1.5">
          {RUBROS.map((r) => (
            <button
              key={r.n}
              type="button"
              onClick={() => setVoz({ emojiRubro: r.e })}
              aria-pressed={(voz.emojiRubro || "") === r.e}
              title={r.n}
              className={`h-10 px-3 rounded-xl border text-sm transition-all focus-visible:ring-2 focus-visible:ring-violet-400 ${
                (voz.emojiRubro || "") === r.e ? "bg-violet-50 border-violet-400" : "bg-white border-slate-200 hover:border-violet-300"
              }`}
            >
              {r.e ? <span className="text-base">{r.e}</span> : <span className="text-xs text-slate-500">Ninguno</span>}
            </button>
          ))}
        </div>
      </Grupo>

      <Grupo titulo="¿Qué querés que haga?" sub="Sin esto el mensaje cuenta una oferta y termina, sin pedir nada.">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {CTAS.map((c) => (
            <button
              key={c.label}
              type="button"
              onClick={() => { setCtaLibre(false); setVoz({ cta: c.txt }); }}
              aria-pressed={!ctaLibre && (voz.cta || "") === c.txt}
              className={`text-left px-3 py-2 rounded-xl border text-sm transition-all focus-visible:ring-2 focus-visible:ring-violet-400 ${
                !ctaLibre && (voz.cta || "") === c.txt ? "bg-violet-50 border-violet-400 text-violet-800 font-semibold" : "bg-white border-slate-200 text-slate-700 hover:border-violet-300"
              }`}
            >
              {c.label}
            </button>
          ))}
          <button
            type="button"
            onClick={() => setCtaLibre(true)}
            aria-pressed={ctaLibre}
            className={`text-left px-3 py-2 rounded-xl border text-sm transition-all ${ctaLibre ? "bg-violet-50 border-violet-400 text-violet-800 font-semibold" : "bg-white border-slate-200 text-slate-700 hover:border-violet-300"}`}
          >
            Escribirlo yo
          </button>
        </div>
        {ctaLibre && (
          <input
            value={voz.cta || ""}
            onChange={(e) => setVoz({ cta: e.target.value })}
            placeholder="Respondé este mensaje y te reservamos la mesa"
            className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
          />
        )}
      </Grupo>

      <Grupo titulo="¿Quién firma?" sub="Opcional. Un nombre propio hace que el mensaje no parezca un robot.">
        <input
          value={voz.firma || ""}
          onChange={(e) => setVoz({ firma: e.target.value })}
          placeholder="— Flor, de HATSU"
          className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
        />
      </Grupo>

      <Grupo titulo="Palabras que NO querés usar" sub="Separadas por coma. Si alguna aparece, el sistema no te deja enviar.">
        <input
          value={prohibidasTxt}
          onChange={(e) => setProhibidasTxt(e.target.value)}
          onBlur={() => setVoz({ prohibidas: prohibidasTxt.split(",").map((p) => p.trim()).filter(Boolean) })}
          placeholder="gratis, regalamos, urgente"
          className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
        />
        <p className="text-[11px] text-slate-500 flex items-start gap-1.5">
          <MessageCircle size={11} className="flex-shrink-0 mt-0.5" />
          No se borran del texto por atrás: te avisamos para que lo cambies vos. Reescribir un
          mensaje sin decírtelo sería mandarle a tus clientes algo que nunca aprobaste.
        </p>
      </Grupo>
    </div>
  );
}
